import { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  TextField, MenuItem, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Checkbox,
  Divider, LinearProgress,
} from '@mui/material';
import {
  ArrowBack, Add, Delete, CompareArrows, CheckCircle,
  RadioButtonUnchecked, Lock, Refresh, LinkOff, Link,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('es-EC') : '—';

// ── Estado de conciliación ────────────────────────────────────────────────────
const ESTADO_CHIP = {
  EN_PROCESO: { label: 'En proceso', color: '#1565c0', bg: '#e3f2fd' },
  CONCILIADO: { label: 'Conciliado ✓', color: '#2e7d32', bg: '#e8f5e9' },
};

// ── Panel header ──────────────────────────────────────────────────────────────
function PanelHeader({ title, subtitle, count, total, color }) {
  return (
    <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color }}>{title}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{count} movimiento(s)</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color }}>{fmt(total)}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Fila del libro (sistema) ──────────────────────────────────────────────────
function FilaLibro({ tx, conciliado, seleccionado, onToggle, disabled }) {
  const tipo = tx.tipo === 'INGRESO' ? 'INGRESO' : tx.tipo === 'EGRESO' ? 'EGRESO' : 'TRANSF.';
  const colorTipo = tipo === 'INGRESO' ? '#2e7d32' : tipo === 'EGRESO' ? '#b71c1c' : '#1565c0';
  return (
    <TableRow
      hover
      sx={{
        opacity: conciliado ? 0.5 : 1,
        bgcolor: seleccionado ? '#e3f2fd' : conciliado ? '#f1f8e9' : 'inherit',
        cursor: disabled ? 'default' : 'pointer',
      }}
      onClick={() => !disabled && !conciliado && onToggle(tx.id)}
    >
      <TableCell padding="checkbox">
        {conciliado
          ? <CheckCircle sx={{ color: '#43a047', fontSize: 18 }} />
          : <Checkbox size="small" checked={seleccionado} disabled={disabled} onChange={() => onToggle(tx.id)} onClick={(e) => e.stopPropagation()} />
        }
      </TableCell>
      <TableCell sx={{ fontSize: 11 }}>{fmtDate(tx.fecha)}</TableCell>
      <TableCell sx={{ fontSize: 11 }}>{tx.concepto || '—'}</TableCell>
      <TableCell sx={{ fontSize: 11 }}>
        <Chip label={tipo} size="small" sx={{ fontSize: 9, height: 18, color: colorTipo, bgcolor: colorTipo + '18', fontWeight: 700 }} />
      </TableCell>
      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: colorTipo }}>{fmt(tx.monto)}</TableCell>
    </TableRow>
  );
}

// ── Fila del banco (extracto) ─────────────────────────────────────────────────
function FilaBanco({ mov, conciliado, seleccionado, onToggle, onEliminar, disabled }) {
  const colorMonto = parseFloat(mov.monto) >= 0 ? '#2e7d32' : '#b71c1c';
  return (
    <TableRow
      hover
      sx={{
        opacity: conciliado ? 0.5 : 1,
        bgcolor: seleccionado ? '#fce4ec' : conciliado ? '#f1f8e9' : 'inherit',
        cursor: disabled ? 'default' : 'pointer',
      }}
      onClick={() => !disabled && !conciliado && onToggle(mov.id)}
    >
      <TableCell padding="checkbox">
        {conciliado
          ? <CheckCircle sx={{ color: '#43a047', fontSize: 18 }} />
          : <Checkbox size="small" checked={seleccionado} disabled={disabled} onChange={() => onToggle(mov.id)} onClick={(e) => e.stopPropagation()} />
        }
      </TableCell>
      <TableCell sx={{ fontSize: 11 }}>{fmtDate(mov.fecha)}</TableCell>
      <TableCell sx={{ fontSize: 11 }}>{mov.descripcion || '—'}</TableCell>
      <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{mov.referencia || '—'}</TableCell>
      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: colorMonto }}>{fmt(Math.abs(mov.monto))}</TableCell>
      <TableCell padding="checkbox">
        {!disabled && !conciliado && (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEliminar(mov.id); }}>
            <Delete sx={{ fontSize: 14, color: '#ef9a9a' }} />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Conciliacion() {
  const navigate = useNavigate();

  // ── Selector ─────────────────────────────────────────────────────────────
  const [bancos, setBancos] = useState([]);
  const [bancosLoading, setBancosLoading] = useState(true);
  const [form, setForm] = useState({ bancoId: '', fechaInicio: '', fechaFin: '', saldoBanco: '' });

  // ── Conciliación activa ───────────────────────────────────────────────────
  const [conciliacion, setConciliacion] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ── Selección para matching ───────────────────────────────────────────────
  const [selLibro, setSelLibro] = useState([]);   // ids
  const [selBanco, setSelBanco] = useState([]);   // ids

  // ── Dialog: agregar movimiento banco ─────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nuevoMov, setNuevoMov] = useState({ fecha: '', descripcion: '', referencia: '', monto: '', tipo: 'CREDITO' });

  // ── Dialog: confirmar cierre ──────────────────────────────────────────────
  const [confirmCierre, setConfirmCierre] = useState(false);

  useEffect(() => { cargarBancos(); }, []);

  const cargarBancos = async () => {
    try {
      setBancosLoading(true);
      const r = await axios.get(`${API}/api/tesoreria/bancos`, cfg());
      setBancos(r.data.data?.filter(b => b.activa !== false) || []);
    } catch (e) {
      setError('Error al cargar bancos');
    } finally {
      setBancosLoading(false);
    }
  };

  const cargarHistorial = async (bancoId) => {
    try {
      const r = await axios.get(`${API}/api/tesoreria/conciliaciones?bancoId=${bancoId}`, cfg());
      setHistorial(r.data.data || []);
    } catch { setHistorial([]); }
  };

  const iniciarConciliacion = async () => {
    if (!form.bancoId || !form.fechaInicio || !form.fechaFin || form.saldoBanco === '') {
      setError('Completa todos los campos para iniciar la conciliación');
      return;
    }
    try {
      setWorking(true);
      setError(null);
      const r = await axios.post(`${API}/api/tesoreria/conciliaciones`, {
        banco_id: form.bancoId,
        fecha_inicio: form.fechaInicio,
        fecha_fin: form.fechaFin,
        saldo_banco: parseFloat(form.saldoBanco),
      }, cfg());
      setConciliacion(r.data.data);
      setSelLibro([]);
      setSelBanco([]);
      setSuccess('Conciliación iniciada');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al iniciar conciliación');
    } finally {
      setWorking(false);
    }
  };

  const abrirHistorial = async (item) => {
    setConciliacion(item);
    setSelLibro([]);
    setSelBanco([]);
  };

  // ── Guardar estado actual en backend ─────────────────────────────────────
  const guardar = async (updates) => {
    if (!conciliacion) return;
    try {
      const r = await axios.put(`${API}/api/tesoreria/conciliaciones/${conciliacion.id}`, updates, cfg());
      setConciliacion(r.data.data);
      return r.data.data;
    } catch (e) {
      setError(e.response?.data?.message || 'Error al guardar');
    }
  };

  // ── Agregar movimiento al extracto bancario ────────────────────────────
  const agregarMovBanco = async () => {
    if (!nuevoMov.fecha || !nuevoMov.monto) return;
    const monto = nuevoMov.tipo === 'DEBITO' ? -Math.abs(parseFloat(nuevoMov.monto)) : Math.abs(parseFloat(nuevoMov.monto));
    const mov = {
      id: `bk-${Date.now()}`,
      fecha: nuevoMov.fecha,
      descripcion: nuevoMov.descripcion,
      referencia: nuevoMov.referencia,
      monto,
      conciliado: false,
    };
    const nuevos = [...(conciliacion.movimientos_banco || []), mov];
    await guardar({ movimientos_banco: nuevos });
    setDialogOpen(false);
    setNuevoMov({ fecha: '', descripcion: '', referencia: '', monto: '', tipo: 'CREDITO' });
  };

  // ── Eliminar movimiento del extracto ─────────────────────────────────────
  const eliminarMovBanco = async (id) => {
    const nuevos = (conciliacion.movimientos_banco || []).filter(m => m.id !== id);
    await guardar({ movimientos_banco: nuevos });
  };

  // ── Conciliar pares seleccionados ─────────────────────────────────────────
  const conciliarSeleccion = async () => {
    if (!selLibro.length || !selBanco.length) {
      setError('Selecciona al menos un movimiento de cada panel');
      return;
    }

    const libros = (conciliacion.movimientos_libro || []).map(t =>
      selLibro.includes(t.id) ? { ...t, conciliado: true } : t
    );
    const bancoMov = (conciliacion.movimientos_banco || []).map(m =>
      selBanco.includes(m.id) ? { ...m, conciliado: true } : m
    );
    const pares = selLibro.map((lId, i) => ({
      libro_id: lId,
      banco_id: selBanco[i] || selBanco[selBanco.length - 1],
      fecha: new Date().toISOString(),
    }));
    const conciliados = [...(conciliacion.movimientos_conciliados || []), ...pares];

    await guardar({ movimientos_libro: libros, movimientos_banco: bancoMov, movimientos_conciliados: conciliados });
    setSelLibro([]);
    setSelBanco([]);
    setSuccess(`${pares.length} par(es) conciliado(s)`);
    setTimeout(() => setSuccess(null), 3000);
  };

  // ── Desconciliar ──────────────────────────────────────────────────────────
  const desconciliarTodo = async () => {
    const libros = (conciliacion.movimientos_libro || []).map(t => ({ ...t, conciliado: false }));
    const bancoMov = (conciliacion.movimientos_banco || []).map(m => ({ ...m, conciliado: false }));
    await guardar({ movimientos_libro: libros, movimientos_banco: bancoMov, movimientos_conciliados: [] });
    setSelLibro([]);
    setSelBanco([]);
  };

  // ── Cerrar conciliación ──────────────────────────────────────────────────
  const cerrarConciliacion = async () => {
    try {
      setWorking(true);
      const r = await axios.post(`${API}/api/tesoreria/conciliaciones/${conciliacion.id}/conciliar`, {}, cfg());
      setConciliacion(r.data.data);
      setConfirmCierre(false);
      setSuccess('¡Conciliación cerrada exitosamente!');
      setTimeout(() => setSuccess(null), 4000);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cerrar conciliación');
      setConfirmCierre(false);
    } finally {
      setWorking(false);
    }
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const mvLibro = conciliacion?.movimientos_libro || [];
  const mvBanco = conciliacion?.movimientos_banco || [];

  const totalLibro = useMemo(() =>
    mvLibro.reduce((s, t) => s + (t.tipo === 'INGRESO' ? parseFloat(t.monto) : t.tipo === 'EGRESO' ? -parseFloat(t.monto) : 0), 0),
    [mvLibro]
  );
  const totalBanco = useMemo(() =>
    mvBanco.reduce((s, m) => s + parseFloat(m.monto || 0), 0),
    [mvBanco]
  );

  const diferencia = conciliacion
    ? parseFloat(conciliacion.saldo_banco) - parseFloat(conciliacion.saldo_libro)
    : 0;
  const diferenciaAbs = Math.abs(diferencia);
  const conciliadoCount = (conciliacion?.movimientos_conciliados || []).length;
  const totalMov = mvLibro.length + mvBanco.length;
  const progreso = totalMov > 0 ? (conciliadoCount / Math.max(mvLibro.length, mvBanco.length, 1)) * 100 : 0;

  const esCerrado = conciliacion?.estado === 'CONCILIADO';

  const toggleSelLibro = (id) => setSelLibro(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelBanco = (id) => setSelBanco(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/tesoreria')} size="small" sx={{ border: '1px solid #e0e0e0' }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#880e4f', letterSpacing: -0.5 }}>
            ⚖️ Conciliación Bancaria
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Compara movimientos del sistema con el extracto bancario
          </Typography>
        </Box>
        {conciliacion && (
          <Chip
            label={ESTADO_CHIP[conciliacion.estado]?.label || conciliacion.estado}
            sx={{
              fontWeight: 700, fontSize: 12,
              bgcolor: ESTADO_CHIP[conciliacion.estado]?.bg,
              color: ESTADO_CHIP[conciliacion.estado]?.color,
            }}
          />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── VISTA: sin conciliación activa ───────────────────────────────── */}
      {!conciliacion && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>

          {/* Formulario nueva conciliación */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 2.5 }}>Nueva Conciliación</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                select fullWidth size="small" label="Banco / Cuenta"
                value={form.bancoId} onChange={(e) => { setForm(f => ({ ...f, bancoId: e.target.value })); cargarHistorial(e.target.value); }}
                disabled={bancosLoading}
              >
                {bancos.map(b => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.nombre_banco} — {b.tipo_cuenta} {b.numero_cuenta ? `(****${String(b.numero_cuenta).slice(-4)})` : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth size="small" label="Saldo según extracto bancario" type="number"
                value={form.saldoBanco} onChange={(e) => setForm(f => ({ ...f, saldoBanco: e.target.value }))}
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, fontSize: 13 }}>$</Typography> }}
              />
              <TextField
                fullWidth size="small" label="Fecha inicio" type="date"
                value={form.fechaInicio} onChange={(e) => setForm(f => ({ ...f, fechaInicio: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth size="small" label="Fecha fin" type="date"
                value={form.fechaFin} onChange={(e) => setForm(f => ({ ...f, fechaFin: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained" onClick={iniciarConciliacion} disabled={working}
                startIcon={working ? <CircularProgress size={16} /> : <CompareArrows />}
                sx={{ bgcolor: '#ad1457', '&:hover': { bgcolor: '#880e4f' }, textTransform: 'none' }}
              >
                Iniciar Conciliación
              </Button>
            </Box>
          </Paper>

          {/* Historial */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 2 }}>Historial</Typography>
            {historial.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 13, textAlign: 'center', py: 3 }}>
                {form.bancoId ? 'Sin conciliaciones para este banco' : 'Selecciona un banco'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {historial.slice().reverse().map(c => (
                  <Box
                    key={c.id}
                    onClick={() => abrirHistorial(c)}
                    sx={{
                      p: 1.5, borderRadius: 1.5, border: '1px solid #e0e0e0',
                      cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                        {fmtDate(c.fecha_inicio)} – {fmtDate(c.fecha_fin)}
                      </Typography>
                      <Chip
                        label={ESTADO_CHIP[c.estado]?.label || c.estado}
                        size="small"
                        sx={{ fontSize: 9, height: 18, bgcolor: ESTADO_CHIP[c.estado]?.bg, color: ESTADO_CHIP[c.estado]?.color }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      Diferencia: <strong style={{ color: Math.abs(c.diferencia || 0) < 0.01 ? '#2e7d32' : '#b71c1c' }}>{fmt(c.diferencia)}</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* ── VISTA: conciliación activa ────────────────────────────────────── */}
      {conciliacion && (
        <>
          {/* Barra de resumen */}
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr) auto' }, gap: 2, alignItems: 'center' }}>
              <Box>
                <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Banco</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {bancos.find(b => b.id === conciliacion.banco_id)?.nombre_banco || conciliacion.banco_id}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  {fmtDate(conciliacion.fecha_inicio)} – {fmtDate(conciliacion.fecha_fin)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Saldo Libro</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1565c0' }}>{fmt(conciliacion.saldo_libro)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Saldo Banco</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#00695c' }}>{fmt(conciliacion.saldo_banco)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Diferencia</Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 900, color: diferenciaAbs < 0.01 ? '#2e7d32' : '#b71c1c' }}>
                  {fmt(diferencia)}
                </Typography>
                {diferenciaAbs < 0.01 && <Typography sx={{ fontSize: 10, color: '#2e7d32', fontWeight: 700 }}>✓ Cuadra</Typography>}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  size="small" variant="outlined"
                  onClick={() => { setConciliacion(null); setSelLibro([]); setSelBanco([]); }}
                  sx={{ textTransform: 'none', fontSize: 11 }}
                >
                  ← Volver
                </Button>
                {!esCerrado && (
                  <Button
                    size="small" variant="contained"
                    onClick={() => setConfirmCierre(true)}
                    disabled={diferenciaAbs > 0.01}
                    startIcon={<Lock />}
                    sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, textTransform: 'none', fontSize: 11 }}
                  >
                    Cerrar
                  </Button>
                )}
              </Box>
            </Box>

            {/* Barra de progreso */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  Progreso: {conciliadoCount} par(es) conciliado(s)
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1565c0' }}>{Math.round(progreso)}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={Math.min(progreso, 100)} sx={{ height: 6, borderRadius: 3, bgcolor: '#e3f2fd', '& .MuiLinearProgress-bar': { bgcolor: '#1565c0' } }} />
            </Box>
          </Paper>

          {/* Toolbar de acciones */}
          {!esCerrado && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                size="small" variant="contained"
                onClick={conciliarSeleccion}
                disabled={!selLibro.length || !selBanco.length}
                startIcon={<Link />}
                sx={{ bgcolor: '#1565c0', textTransform: 'none', fontSize: 12 }}
              >
                Conciliar par ({selLibro.length} + {selBanco.length})
              </Button>
              <Button
                size="small" variant="outlined"
                onClick={() => setDialogOpen(true)}
                startIcon={<Add />}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Agregar línea bancaria
              </Button>
              <Button
                size="small" variant="outlined" color="error"
                onClick={desconciliarTodo}
                startIcon={<LinkOff />}
                sx={{ textTransform: 'none', fontSize: 12 }}
              >
                Desconciliar todo
              </Button>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', alignSelf: 'center' }}>
                Selecciona movimientos en ambos paneles y haz clic en "Conciliar par"
              </Typography>
            </Box>
          )}

          {/* Dos paneles */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>

            {/* Panel izquierdo: Libro (sistema) */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <PanelHeader
                title="📒 Movimientos del Sistema"
                subtitle="Transacciones registradas en el período"
                count={mvLibro.length}
                total={totalLibro}
                color="#1565c0"
              />
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#e3f2fd', fontSize: 11, fontWeight: 700 } }}>
                      <TableCell padding="checkbox" />
                      <TableCell>Fecha</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mvLibro.length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: 12 }}>Sin transacciones en el período</TableCell></TableRow>
                    ) : (
                      mvLibro.map(tx => (
                        <FilaLibro
                          key={tx.id}
                          tx={tx}
                          conciliado={!!tx.conciliado}
                          seleccionado={selLibro.includes(tx.id)}
                          onToggle={toggleSelLibro}
                          disabled={esCerrado}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Panel derecho: Extracto bancario */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <PanelHeader
                title="🏦 Extracto Bancario"
                subtitle="Movimientos según el banco"
                count={mvBanco.length}
                total={totalBanco}
                color="#00695c"
              />
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#e0f2f1', fontSize: 11, fontWeight: 700 } }}>
                      <TableCell padding="checkbox" />
                      <TableCell>Fecha</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Ref.</TableCell>
                      <TableCell align="right">Monto</TableCell>
                      <TableCell padding="checkbox" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mvBanco.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: 12 }}>
                          Sin líneas del extracto — agrega líneas con el botón "Agregar línea bancaria"
                        </TableCell>
                      </TableRow>
                    ) : (
                      mvBanco.map(mov => (
                        <FilaBanco
                          key={mov.id}
                          mov={mov}
                          conciliado={!!mov.conciliado}
                          seleccionado={selBanco.includes(mov.id)}
                          onToggle={toggleSelBanco}
                          onEliminar={eliminarMovBanco}
                          disabled={esCerrado}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!esCerrado && (
                <Box sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<Add />} onClick={() => setDialogOpen(true)} sx={{ textTransform: 'none', fontSize: 11, color: '#00695c' }}>
                    Agregar línea
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        </>
      )}

      {/* ── Dialog: agregar movimiento bancario ──────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Agregar Línea del Extracto</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select fullWidth size="small" label="Tipo"
              value={nuevoMov.tipo} onChange={(e) => setNuevoMov(m => ({ ...m, tipo: e.target.value }))}
            >
              <MenuItem value="CREDITO">Crédito (ingreso al banco)</MenuItem>
              <MenuItem value="DEBITO">Débito (egreso del banco)</MenuItem>
            </TextField>
            <TextField
              fullWidth size="small" label="Fecha" type="date"
              value={nuevoMov.fecha} onChange={(e) => setNuevoMov(m => ({ ...m, fecha: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth size="small" label="Monto" type="number"
              value={nuevoMov.monto} onChange={(e) => setNuevoMov(m => ({ ...m, monto: e.target.value }))}
              InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, fontSize: 13 }}>$</Typography> }}
            />
            <TextField
              fullWidth size="small" label="Descripción"
              value={nuevoMov.descripcion} onChange={(e) => setNuevoMov(m => ({ ...m, descripcion: e.target.value }))}
            />
            <TextField
              fullWidth size="small" label="Referencia / N° documento"
              value={nuevoMov.referencia} onChange={(e) => setNuevoMov(m => ({ ...m, referencia: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained" onClick={agregarMovBanco}
            disabled={!nuevoMov.fecha || !nuevoMov.monto}
            sx={{ bgcolor: '#00695c', textTransform: 'none' }}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: confirmar cierre ──────────────────────────────────────── */}
      <Dialog open={confirmCierre} onClose={() => setConfirmCierre(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Cerrar Conciliación</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 1 }}>
            La diferencia es <strong>{fmt(diferencia)}</strong> — la conciliación está cuadrada.
          </Alert>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Una vez cerrada no podrás modificar los movimientos. ¿Confirmas el cierre?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCierre(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained" onClick={cerrarConciliacion} disabled={working}
            startIcon={working ? <CircularProgress size={16} /> : <Lock />}
            sx={{ bgcolor: '#2e7d32', textTransform: 'none' }}
          >
            Cerrar conciliación
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
